import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useRouter } from '../router/RouterContext';

const DOCS = [
  { id: 'terms', label: '이용약관', file: '/legal/terms.md' },
  { id: 'privacy', label: '개인정보처리방침', file: '/legal/privacy.md' },
  { id: 'community', label: '커뮤니티 이용규칙', file: '/legal/community.md' },
  { id: 'location', label: '위치정보 이용약관', file: '/legal/location.md' },
];

function getDocId(): string {
  const params = new URLSearchParams(window.location.search);
  const doc = params.get('doc');
  return DOCS.find((d) => d.id === doc) ? doc! : 'terms';
}

const LegalPage: React.FC = () => {
  const [activeId, setActiveId] = useState<string>(getDocId);
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const activeDoc = DOCS.find((d) => d.id === activeId)!;

  // Sync tab when browser back/forward changes the query string within /legal
  useEffect(() => {
    const onPopState = () => setActiveId(getDocId());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const handleTabClick = (id: string) => {
    setActiveId(id);
    window.history.pushState({}, '', `/legal?doc=${id}`);
  };

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(activeDoc.file)
      .then((res) => {
        if (!res.ok) throw new Error('fetch failed');
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [activeDoc.file]);

  return (
    <main
      style={{
        maxWidth: '52rem',
        margin: '0 auto',
        padding: '3rem 1.5rem 6rem',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <header style={{ marginBottom: '2rem' }}>
        <p
          style={{
            fontSize: '0.65rem',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
            margin: '0 0 0.5rem',
          }}
        >
          Noraneko · Legal
        </p>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2rem, 6vw, 3.5rem)',
            letterSpacing: '0.04em',
            margin: 0,
            color: 'var(--text)',
          }}
        >
          법무 문서
        </h1>
      </header>

      {/* Tab bar */}
      <div
        role="tablist"
        aria-label="법무 문서 탭"
        style={{
          display: 'flex',
          gap: '0',
          borderBottom: '1px solid var(--stroke)',
          marginBottom: '2rem',
          overflowX: 'auto',
        }}
      >
        {DOCS.map((doc) => {
          const isActive = doc.id === activeId;
          return (
            <button
              key={doc.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => handleTabClick(doc.id)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--accent-yellow)' : '2px solid transparent',
                padding: '0.6rem 1rem',
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.04em',
                color: isActive ? 'var(--text)' : 'var(--muted)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s, border-color 0.15s',
                marginBottom: '-1px',
              }}
            >
              {doc.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div
        role="tabpanel"
        aria-label={activeDoc.label}
        style={{
          background: 'var(--card)',
          border: '1px solid var(--stroke)',
          borderRadius: 'var(--radius)',
          padding: '2rem',
        }}
      >
        {loading && (
          <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>불러오는 중…</p>
        )}
        {error && (
          <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>문서를 불러올 수 없습니다.</p>
        )}
        {!loading && !error && (
          <div className="legal-prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
        )}
      </div>

      <style>{`
        .legal-prose {
          color: var(--text);
          font-size: 0.9rem;
          line-height: 1.8;
        }
        .legal-prose h1,
        .legal-prose h2,
        .legal-prose h3 {
          font-family: var(--font-body);
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 0.5rem;
          color: var(--text);
        }
        .legal-prose h1 { font-size: 1.3rem; }
        .legal-prose h2 { font-size: 1.1rem; }
        .legal-prose h3 { font-size: 1rem; }
        .legal-prose p { margin: 0.75rem 0; }
        .legal-prose ul,
        .legal-prose ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .legal-prose li { margin: 0.25rem 0; }
        .legal-prose table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8rem;
          margin: 1rem 0;
        }
        .legal-prose th,
        .legal-prose td {
          border: 1px solid var(--stroke);
          padding: 0.4rem 0.75rem;
          text-align: left;
        }
        .legal-prose th {
          background: var(--bg-elevated);
          font-weight: 600;
        }
        .legal-prose hr {
          border: none;
          border-top: 1px solid var(--stroke);
          margin: 1.5rem 0;
        }
        .legal-prose strong { font-weight: 700; }
        .legal-prose a {
          color: var(--accent-blue);
          text-decoration: underline;
        }
      `}</style>
    </main>
  );
};

export default LegalPage;
